import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNowStrict } from 'date-fns';
import {
  Activity,
  AlertTriangle,
  ArrowUpRight,
  Bot,
  CheckCircle2,
  Clock3,
  MessageSquare,
  RefreshCw,
  Users,
} from 'lucide-react';
import { dashboardApi } from '../utils/api';
import { Loader } from '../components/Loader';
import { cn } from '../components/ui';
import type { AgentOverviewData, AgentOverviewClient, AgentOverviewRun } from '../types';

function timeAgo(value: string | null) {
  if (!value) return 'No activity yet';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Time unavailable' : formatDistanceToNowStrict(date, { addSuffix: true });
}

function connectionLabel(status: AgentOverviewClient['ghl_status']) {
  if (status === 'connected') return 'GHL live';
  if (status === 'needs_attention') return 'Check token';
  if (status === 'timeout') return 'GHL timed out';
  if (status === 'not_configured') return 'Not connected';
  return 'GHL unavailable';
}

function errorMessage(error: any) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((item: any) => item?.msg).filter(Boolean).join('. ') || 'Invalid dashboard request';
  return 'Could not load agent overview';
}

function Metric({ label, value, note, icon: Icon, accent }: {
  label: string; value: number; note: string; icon: typeof Activity; accent: string;
}) {
  return (
    <article className="group rounded-2xl border border-border bg-card p-5 shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground">{label}</p>
          <p className="mt-3 text-3xl font-semibold tracking-tight tabular-nums">{value.toLocaleString()}</p>
        </div>
        <span className={cn('rounded-xl p-3', accent)}><Icon className="h-5 w-5" /></span>
      </div>
      <p className="mt-3 text-xs text-muted-foreground">{note}</p>
    </article>
  );
}

function ClientRow({ client }: { client: AgentOverviewClient }) {
  const channels = Object.entries(client.channels).filter(([, count]) => count > 0);
  return (
    <Link to={`/sub-account/${client.id}`} className="grid grid-cols-1 gap-4 rounded-xl border border-border/70 bg-background p-4 transition-colors hover:border-primary/40 hover:bg-muted/20 md:grid-cols-[minmax(180px,1.5fr)_minmax(150px,1fr)_minmax(170px,1fr)_minmax(140px,1fr)_auto] md:items-center">
      <div className="flex min-w-0 items-center gap-3">
        <div className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-slate-900 text-white"><span className="text-sm font-semibold">{client.name.slice(0, 1).toUpperCase()}</span></div>
        <div className="min-w-0">
          <p className="truncate font-semibold">{client.name}</p>
          <p className="truncate text-xs text-muted-foreground">/{client.slug}</p>
        </div>
      </div>
      <div>
        <p className="text-lg font-semibold tabular-nums">{client.conversation_count.toLocaleString()}</p>
        <p className="text-xs text-muted-foreground">GHL recent conversations</p>
      </div>
      <div>
        <p className={cn('text-lg font-semibold tabular-nums', client.needs_reply_count ? 'text-amber-700 dark:text-amber-400' : '')}>{client.needs_reply_count}</p>
        <p className="text-xs text-muted-foreground">latest message inbound</p>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {channels.length ? channels.map(([name, count]) => (
          <span key={name} className="rounded-full bg-muted px-2 py-1 text-[11px] font-medium text-muted-foreground">{name} {count}</span>
        )) : <span className="text-xs text-muted-foreground">No recent channels</span>}
      </div>
      <div className="flex items-center gap-2 md:justify-end">
        <span className={cn('h-2 w-2 rounded-full', client.ghl_status === 'connected' ? 'bg-emerald-500' : client.ghl_status === 'needs_attention' ? 'bg-amber-500' : 'bg-slate-300')} />
        <span className="whitespace-nowrap text-xs font-medium">{connectionLabel(client.ghl_status)}</span>
        <ArrowUpRight className="ml-1 h-4 w-4 text-muted-foreground" />
      </div>
      <div className="md:col-span-full flex items-center justify-between border-t border-border/60 pt-3 text-xs text-muted-foreground">
        <span className="flex items-center gap-1.5"><Clock3 className="h-3.5 w-3.5" /> Last GHL activity {timeAgo(client.last_activity_at)}</span>
        <span className="flex items-center gap-1.5">
          <Bot className="h-3.5 w-3.5" />
          {client.worker_enabled ? `App worker · ${client.replies_24h} replies / 24h` : 'App worker paused'}
          {client.failed_runs_24h > 0 && <span className="ml-1 text-rose-600">· {client.failed_runs_24h} failed runs</span>}
        </span>
      </div>
    </Link>
  );
}

function RunRow({ run }: { run: AgentOverviewRun }) {
  const good = run.status === 'success';
  const running = run.status === 'running';
  return (
    <div className="flex items-start gap-3 border-b border-border/70 py-3 last:border-0">
      <span className={cn('mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-lg', good ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : running ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400' : 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400')}>
        {good ? <CheckCircle2 className="h-4 w-4" /> : running ? <Activity className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-2">
          <p className="truncate text-sm font-semibold">{run.client_name}</p>
          <span className="whitespace-nowrap text-[11px] text-muted-foreground">{timeAgo(run.started_at)}</span>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          {run.status} · {run.messages_processed} processed · {run.messages_sent} sent
          {run.error_count > 0 ? ` · ${run.error_count} errors` : ''}
        </p>
      </div>
    </div>
  );
}



export function DashboardPage() {
  const [data, setData] = useState<AgentOverviewData | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (quiet = false) => {
    try {
      quiet ? setRefreshing(true) : setLoading(true);
      setError(null);
      const response = await dashboardApi.getAgentOverview();
      setData(response.data);
    } catch (err: any) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const timer = window.setInterval(() => void load(true), 90_000);
    return () => window.clearInterval(timer);
  }, [load]);

  if (loading && !data) return <Loader fullScreen />;
  if (error && !data) {
    return <div className="mx-auto max-w-xl py-16 text-center">
      <AlertTriangle className="mx-auto mb-4 h-12 w-12 text-rose-500" />
      <h2 className="mb-2 text-xl font-semibold">Agent overview is unavailable</h2>
      <p className="mb-5 text-muted-foreground">{error}</p>
      <button onClick={() => void load()} className="rounded-lg bg-primary px-4 py-2 text-primary-foreground">Try again</button>
    </div>;
  }

  const summary = data?.summary;
  const clients = data?.clients ?? [];
  const runs = data?.recent_runs ?? [];
  const metrics = [
    { label: 'GHL recent conversations', value: summary?.ghl_total_conversations ?? 0, note: `${summary?.ghl_connected_clients ?? 0} client locations connected`, icon: MessageSquare, accent: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300' },
    { label: 'Waiting on a reply', value: summary?.awaiting_reply ?? 0, note: 'Latest message from the contact', icon: Clock3, accent: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
    { label: 'Instagram / Facebook', value: (summary?.instagram_threads_sampled ?? 0) + (summary?.facebook_threads_sampled ?? 0), note: `${summary?.instagram_threads_sampled ?? 0} Instagram · ${summary?.facebook_threads_sampled ?? 0} Facebook in latest sample`, icon: Activity, accent: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300' },
    { label: 'Recorded worker replies', value: summary?.worker_replies_24h ?? 0, note: `${summary?.worker_runs_24h ?? 0} runs · ${summary?.failed_worker_runs_24h ?? 0} failed in 24h`, icon: Bot, accent: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' },
  ];

  return (
    <div className="space-y-7">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-[0.2em] text-primary">Agency operations / live view</p>
          <h1 className="text-3xl font-bold tracking-tight sm:text-4xl">Agent overview</h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">Aggregate GoHighLevel and agent activity across your client accounts.</p>
        </div>
        <button onClick={() => void load(true)} disabled={refreshing} className="inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 text-sm font-semibold shadow-sm transition hover:bg-muted disabled:opacity-60">
          <RefreshCw className={cn('h-4 w-4', refreshing && 'animate-spin')} /> Refresh view
        </button>
      </header>

      <section className="relative overflow-hidden rounded-3xl bg-[#10212a] px-6 py-7 text-white shadow-xl sm:px-8 sm:py-8">
        <div className="pointer-events-none absolute -right-12 -top-28 h-72 w-72 rounded-full border-[40px] border-[#b7e4c7]/10" />
        <div className="pointer-events-none absolute right-20 top-10 h-28 w-28 rounded-full bg-[#b7e4c7]/10 blur-2xl" />
        <div className="relative flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-xs text-white/75">
              <span className="relative flex h-2 w-2"><span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-300 opacity-60" /><span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-300" /></span>
              GHL account overview
            </div>
            <h2 className="max-w-2xl text-2xl font-semibold leading-tight tracking-tight sm:text-3xl">A health view across every client account.</h2>
            <p className="mt-2 max-w-xl text-sm leading-6 text-white/65">Client-level counts, connection status, and recorded agent runs help you spot accounts that need attention.</p>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:min-w-[330px]">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <p className="text-xs uppercase tracking-wider text-white/50">Clients connected</p>
              <p className="mt-2 text-2xl font-semibold tabular-nums">{summary?.ghl_connected_clients ?? 0}<span className="ml-1 text-sm font-normal text-white/50">/ {summary?.clients ?? 0}</span></p>
            </div>
            <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
              <p className="text-xs uppercase tracking-wider text-white/50">Last refreshed</p>
              <p className="mt-2 text-sm font-semibold">{data?.updated_at ? timeAgo(data.updated_at) : '—'}</p>
            </div>
          </div>
        </div>
      </section>

      {error && <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-200">{error} Showing the last successful snapshot.</div>}

      <section aria-label="Activity summary" className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {metrics.map(metric => <Metric key={metric.label} {...metric} />)}
      </section>

      <section className="grid gap-6 xl:grid-cols-[minmax(0,1.65fr)_minmax(320px,0.8fr)]">
        <div className="rounded-2xl border border-border bg-card shadow-sm">
          <div className="flex flex-col gap-2 border-b border-border px-5 py-5 sm:flex-row sm:items-center sm:justify-between sm:px-6">
            <div>
              <h2 className="font-semibold">Client pulse</h2>
              <p className="mt-1 text-sm text-muted-foreground">Client-scoped inbox and platform worker summaries.</p>
            </div>
            <span className="inline-flex items-center gap-2 text-xs text-muted-foreground"><Users className="h-4 w-4" /> {clients.length} clients</span>
          </div>
          <div className="space-y-3 p-4 sm:p-5">
            {clients.length ? clients.map(client => <ClientRow key={client.id} client={client} />) : (
              <div className="py-12 text-center text-sm text-muted-foreground">Add a client account to begin monitoring activity.</div>
            )}
          </div>
          <div className="border-t border-border px-5 py-3 text-xs leading-relaxed text-muted-foreground sm:px-6">{data?.activity_scope}</div>
        </div>

        <aside className="rounded-2xl border border-border bg-card shadow-sm">
          <div className="border-b border-border px-5 py-5">
            <div className="flex items-center gap-2"><Activity className="h-4 w-4 text-primary" /><h2 className="font-semibold">Latest worker runs</h2></div>
            <p className="mt-1 text-sm text-muted-foreground">Runs recorded by this platform in the last available history.</p>
          </div>
          <div className="px-5">
            {runs.length ? runs.map(run => <RunRow key={run.id} run={run} />) : (
              <div className="py-10 text-center">
                <div className="mx-auto mb-3 grid h-11 w-11 place-items-center rounded-xl bg-muted"><Bot className="h-5 w-5 text-muted-foreground" /></div>
                <p className="text-sm font-medium">No worker runs recorded</p>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">Independent agents using the GHL CLI are not attributed as app worker runs.</p>
              </div>
            )}
          </div>
          <div className="border-t border-border px-5 py-4 text-xs leading-relaxed text-muted-foreground">
            GoHighLevel inbox activity is live. This run list reflects only jobs that write to this app’s worker log; Hermes, OpenFlow, or other agents need an event/reporting connection to appear here.
          </div>
        </aside>
      </section>


    </div>
  );
}
